import { Box, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import React, {
  Dispatch,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import produce from 'immer';
import { CareTeamInfoExpanded } from '../care-team/channel/CareTeamInfo';
import { Messaging } from '../care-team/messaging/Messaging';
import { useCurrentChannel } from '../care-team/hooks/route/useCurrentChannel';
import { MessagingHeader } from '../care-team/messaging/MessagingHeader';
import {
  CareTeamTab,
  CommunityTab,
  MessageTab,
} from '../care-team/message-bar/MessageBarTabView';
import { MessageBarEmbedded } from '../care-team/message-bar/MessageBarEmbedded';
import { FileGallery } from '../care-team/messaging/gallery/FileGallery';
import { FunctionBuilder, Routes } from '@xolv-mybrightlink/common';
import { messageNavigationState } from './MessageContainerNavigationState';
import { useCareTeamSlaStatus } from '../care-team/hooks/store/useCareTeamSla';
import { ChannelMembers } from '../care-team/messaging/member/ChannelMembers';
import { useCommunityOptInStatus } from '../care-team/hooks/store/useCommunitySla';
import { CommunityOptIn } from '../community/CommunityOptIn';
import { useMessageStandaloneDisplayConfig } from '../care-team/message-bar/MessageStandaloneDisplayConfigProvider';
import { MessageStandaloneDisplayConfigAction } from '../care-team/hooks/store/useMessageStandaloneDisplayReducer';
import { MessageBarExchangeMode } from '../care-team/message-bar/MessageBarStandalone';

export type MessageExpandedViewMode =
  | 'none'
  | 'info'
  | 'conversation'
  | 'care-team'
  | 'community'
  | 'community-sla'
  | 'gallery'
  | 'members';

const NoSelection = () => {
  const intl = useIntl();
  return (
    <Box
      display='flex'
      justifyContent='center'
      alignItems='center'
      height='100%'
    >
      <Typography alignSelf='center' textAlign='center' fontSize={20}>
        {intl.formatMessage({ id: 'messages.no-selection' })}
      </Typography>
    </Box>
  );
};

export const MessageContainer = (props: {
  viewMode: MessageExpandedViewMode;
}) => {
  const { viewMode } = props;
  const slaAccepted = useCareTeamSlaStatus();
  const communityOptedIn = useCommunityOptInStatus();
  const navigate = useNavigate();
  const channel = useCurrentChannel();
  const [patientGuid, setPatientGuid] = useState<string>();
  const [tab, setTab] = useState<MessageTab>(CareTeamTab);
  const { displayConfigDispatch } = useMessageStandaloneDisplayConfig();
  const onChangeTab = (event: React.SyntheticEvent, newTab: MessageTab) => {
    setTab(newTab);
  };
  useEffect(() => {
    if (
      viewMode === 'community' ||
      viewMode === 'community-sla' ||
      channel?.type === 'PEER_TO_PEER'
    ) {
      setTab(CommunityTab);
    } else {
      setTab(CareTeamTab);
    }
  }, [channel, viewMode]);
  const [selectedChannelGuid, setSelectedChannelGuid] = useState<
    string | undefined
  >(channel?.guid);
  const channelHistory = useRef<{ [patientGuid: string]: string }>({});

  const onCollapse = useCallback(() => {
    toMessageBarExchangeState(
      viewMode,
      selectedChannelGuid,
      tab,
      displayConfigDispatch,
    );
    navigate(messageNavigationState.collapsedPath ?? Routes.authenticated);
  }, [navigate, viewMode, selectedChannelGuid, tab, displayConfigDispatch]);

  const toggleInfo = useCallback(() => {
    if (viewMode !== 'info') {
      navigate(Routes.messages.info);
    } else if (selectedChannelGuid) {
      navigate(-1);
    } else {
      navigate(Routes.messages.index);
    }
  }, [viewMode, navigate, selectedChannelGuid]);

  const openMessaging = useCallback(
    (channelGuid: string) => {
      setSelectedChannelGuid(channelGuid);
      navigate(Routes.messages.channel.conversation(channelGuid));
    },
    [navigate],
  );

  const openMessagingForPatient = useCallback(
    (patientGuid: string) => {
      setPatientGuid(patientGuid);
      const channelGuid = channelHistory.current[patientGuid];
      setSelectedChannelGuid(channelGuid);
      if (channelGuid) {
        openMessaging(channelGuid);
      }
    },
    [channelHistory, openMessaging],
  );

  useEffect(() => {
    if (!slaAccepted) {
      navigate(Routes.messages.careTeam.sla, { replace: true });
    }
  }, [slaAccepted, navigate]);

  useEffect(() => {
    if (!communityOptedIn && viewMode === 'community') {
      navigate(Routes.messages.community.sla, { replace: true });
    }
  }, [communityOptedIn, viewMode, navigate]);

  useEffect(() => {
    if (channel) {
      if (channel.type !== 'PEER_TO_PEER') {
        channelHistory.current = produce(channelHistory.current, (draft) => {
          draft[channel.attributes.patientGuid] = channel.guid;
        });
      }
      setSelectedChannelGuid(channel.guid);
    }
  }, [channel]);

  if (!slaAccepted) {
    return null;
  }

  return (
    <Box height='100vh' display='flex'>
      <MessageBarEmbedded
        defaultTab={tab}
        onChangeTab={onChangeTab}
        infoIsOpen={viewMode === 'info'}
        toggleInfo={toggleInfo}
        onCollapse={onCollapse}
        defaultPatientGuid={patientGuid}
        onSelectChannel={openMessaging}
        onSelectPatient={openMessagingForPatient}
      />
      <Box
        className='hide-scrollbar'
        sx={{
          overflow: 'auto',
          width: '100%',
          height: '100%',
          bgcolor: '#f7f8fb',
        }}
      >
        <FunctionBuilder>
          {() => {
            if (viewMode === 'none') {
              return <NoSelection />;
            } else if (viewMode === 'info') {
              return <CareTeamInfoExpanded onClose={toggleInfo} />;
            } else if (
              viewMode === 'conversation' &&
              selectedChannelGuid &&
              channel
            ) {
              return (
                <Messaging
                  key={channel.guid}
                  channelGuid={channel.guid}
                  header={<MessagingHeader channelGuid={channel.guid} />}
                />
              );
            } else if (viewMode === 'community-sla') {
              return <CommunityOptIn />;
            } else if (
              viewMode === 'gallery' &&
              selectedChannelGuid &&
              channel
            ) {
              return <FileGallery channelGuid={channel.guid} />;
            } else if (
              viewMode === 'members' &&
              selectedChannelGuid &&
              channel
            ) {
              return <ChannelMembers channelGuid={channel.guid} />;
            }
            return <NoSelection />;
          }}
        </FunctionBuilder>
      </Box>
    </Box>
  );
};

const toMessageBarExchangeState = (
  viewMode: MessageExpandedViewMode,
  channelGuid: string | undefined,
  tab: MessageTab,
  displayConfigDispatch: Dispatch<MessageStandaloneDisplayConfigAction>,
): void => {
  if (channelGuid) {
    let mode: MessageBarExchangeMode = 'messaging';
    if (viewMode === 'gallery') {
      mode = 'gallery';
    } else if (viewMode === 'members') {
      mode = 'members';
    }
    displayConfigDispatch({
      type: 'CHANGE_VIEW_MODE',
      payload: {
        viewMode: mode,
        activeTab: tab,
        selectedChannelGuid: channelGuid,
      },
    });
  } else {
    displayConfigDispatch({
      type: 'CHANGE_VIEW_MODE',
      payload: {
        viewMode: viewMode === 'info' ? viewMode : 'channels',
        activeTab: tab,
      },
    });
  }
};
